import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';
import PropTypes from 'prop-types';

import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  message,
  Row,
  Space,
  Table,
} from 'antd';
import {AiFillEye} from 'react-icons/all';
import OrderInfo from './OrderInfo';
import ProcedureSelect from 'pages/components/ProcedureSelect';
import {findOrdersByRequestId} from 'services/RequestService';
import {findOrders} from 'services/OrderService';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';
import {EQUAL, JSON_EXTRACT_EQUAL} from 'shared/constants/QueryModes';

const OrderList = (props) => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const {patientId, requestId} = props;

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const [orderId, setOrderId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });

  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    if (requestId?.length > 0) {
      setLoading(true);
      findOrdersByRequestId(headers, requestId, {
        pagination: params.pagination,
        filters: [],
        sorters: params.sorters,
      })
        .then((res) => {
          setOrders(res.data);

          setPagination({
            ...params.pagination,
            total: res.headers[X_TOTAL_COUNT]
              ? parseInt(res.headers[X_TOTAL_COUNT])
              : 0,
          });
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    } else if (patientId?.length > 0) {
      setLoading(true);
      const filters = [];

      if (searchValues?.procedure) {
        filters.push({
          fields: 'procedure',
          mode: JSON_EXTRACT_EQUAL,
          value: ['id', searchValues.procedure.id],
        });
      }

      if (searchValues?.performedDate) {
        filters.push({
          fields: 'performedDate',
          mode: EQUAL,
          value: searchValues.performedDate.format('YYYY-MM-DD'),
        });
      }

      if (params.filters) {
        Object.keys(params.filters).map((key) =>
          filters.push({
            fields: key,
            value: params.filters[key],
          }),
        );
      }

      filters.push({
        fields: 'request.patient.id',
        mode: EQUAL,
        value: patientId,
      });

      findOrders(headers, {
        pagination: params.pagination,
        filters,
        sorters: params.sorters,
      })
        .then((res) => {
          setOrders(res.data);

          setPagination({
            ...params.pagination,
            total: res.headers[X_TOTAL_COUNT]
              ? parseInt(res.headers[X_TOTAL_COUNT])
              : 0,
          });
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    loadData({pagination});
  }, [patientId, requestId]);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (id) => {
    setOrderId(id);
    setViewing(true);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const columns = [
    {
      title: intl.messages['order.procedure'],
      dataIndex: 'procedure',
      key: 'procedure',
      render: (procedure) => (
        <span>
          {procedure.id} - {procedure.name}
        </span>
      ),
    },
    {
      title: intl.messages['order.performedDate'],
      dataIndex: 'performedDate',
      key: 'performedDate',
      sorter: true,
    },
    {
      title: intl.messages['order.result'],
      dataIndex: 'result',
      key: 'result',
      render: (result) => <span>{result}</span>,
    },
    {
      title: intl.messages['order.requester'],
      dataIndex: 'requester',
      key: 'requester',
      render: (requester) => (
        <Button
          type='link'
          onClick={() => navigate(`/health/practitioners/${requester.id}`)}>
          {requester?.name}
        </Button>
      ),
    },
    {
      title: intl.messages['order.performer'],
      dataIndex: 'performer',
      key: 'performer',
      render: (performer) => (
        <Button
          type='link'
          onClick={() => navigate(`/health/organizations/${performer.id}`)}>
          {performer?.name}
        </Button>
      ),
    },
    {
      title: intl.messages['common.actions'],
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right',
      render: (id) => (
        <Space>
          <Button
            type='primary'
            shape='circle'
            ghost
            icon={<AiFillEye />}
            onClick={() => onView(id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      {patientId?.length > 0 && (
        <div>
          <Form
            layout='vertical'
            form={form}
            initialValues={searchValues}
            onFinish={onSearch}>
            <Row gutter={16}>
              <Col span={6} />
              <Col span={6}>
                <Form.Item
                  name={['procedure', 'id']}
                  label={intl.messages['order.procedure']}>
                  <ProcedureSelect
                    onChange={(value) => {
                      form.setFieldsValue({id: value});
                    }}
                    onClear={() => {
                      form.setFieldsValue({id: null});
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name='performedDate'
                  label={intl.messages['order.performedDate']}>
                  <DatePicker
                    format='DD/MM/YYYY'
                    style={{width: '100%'}}
                    allowClear={true}
                  />
                </Form.Item>
              </Col>
              <Col span={6} />
            </Row>
            <Row gutter={16}>
              <Col span={10} />
              <Col span={2}>
                <Button onClick={onClearSearch} style={{width: '100%'}}>
                  {intl.messages['common.reset']}
                </Button>
              </Col>
              <Col span={2}>
                <Button
                  type='primary'
                  htmlType='submit'
                  style={{width: '100%'}}>
                  {intl.messages['common.search']}
                </Button>
              </Col>
              <Col span={10} />
            </Row>
          </Form>
          <Divider />
        </div>
      )}

      <Table
        loading={loading}
        columns={columns}
        dataSource={orders}
        rowKey={(record) => record.id}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />

      <OrderInfo
        orderId={orderId}
        viewing={viewing}
        setViewing={setViewing}
        setReloading={setReloading}
      />
    </>
  );
};

export default OrderList;

OrderList.propTypes = {
  patientId: PropTypes.string,
  requestId: PropTypes.string,
};
